import React, { useState, useEffect } from 'react';
import { Filter, Plus, Edit, X, AlertCircle, CheckCircle, Calendar, Upload, Printer, Pencil, Undo2 } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from "../StoreContext/LanguageContext";

const DirectPayment = ({ onback }) => {
  const isFirstRender = React.useRef(true);
  const [savingProposal, setSavingProposal] = useState(false);
  const { language, translations } = useLanguage();
  // State for data and loading
  const [directPurchaseData, setDirectPurchaseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [isProposalEditing, setIsProposalEditing] = useState(false);
  const [proposalEditData, setProposalEditData] = useState({
    section11_rehabilitation_rights_notice: "",
    direct_purchase_deputy_register_no: "",
    direct_purchase_deputy_register_name: ""
  });

  // Market value loading state
  const [loadingMarketValue, setLoadingMarketValue] = useState(false);
  const surveyNoTimeoutRef = React.useRef(null);

  // Undo state: stores last action for undo capability
  const [undoStack, setUndoStack] = useState([]);
  // undoStack item: { type: 'add'|'edit', recordId, previousData, newData }

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [availableTalukas, setAvailableTalukas] = useState([]);
  const [availableVillages, setAvailableVillages] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluka, setSelectedTaluka] = useState('');
  const [selectedVillage, setSelectedVillage] = useState('');
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingTalukas, setLoadingTalukas] = useState(false);
  const [loadingVillages, setLoadingVillages] = useState(false);
  // Filter-specific
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterTaluka, setFilterTaluka] = useState('');
  const [filterVillage, setFilterVillage] = useState('');
  const [filterReadyToLoad, setFilterReadyToLoad] = useState(false);
  const proposalDefaultsRef = React.useRef(null);
  const [isMobileValid, setIsMobileValid] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  // Dropdown data
  const [areaUnits, setAreaUnits] = useState([]);
  const [landTypes, setLandTypes] = useState([]);

  // Form data
  const [contactError, setContactError] = useState('');
  const [fieldTouched, setFieldTouched] = useState({});
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const [touched, setTouched] = useState({});
  const [proposalDetails, setProposalDetails] = useState(null);

  const ROLES = {
    COLLECTOR: 1,
    LAO: 3,
    SAMANVAY: 6,
    PROJECT_INCHARGE: 18,
    DATA_ENTRY: 14
  };

  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

  const [formData, setFormData] = useState({
    registration_date: "",
    registration_no: "",
    owner_name: "",
    owner_address: "",
    payee_name: "",
    payee_address: "",
    survey_no: "",
    irrigable_area: "",
    size_rupees: "",
    area_sold: "",
    selling_price: "",
    hectare_rate: "",
    hectare_rate_amount: "",
    size_gat: "",
    remarks: "",
    dist_name: "",
    taluka_name: "",
    village_name: "",
  });

  // localostorage.selectedProposal 
  useEffect(() => {
    try {
      const stored = localStorage.getItem("selectedProposal");
      if (stored) {
        setProposalDetails(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Error parsing selectedProposal:", e);
    }
  }, []);

  const [formErrors, setFormErrors] = useState({});
  // Get project and proposal IDs from localStorage
  const projectId = localStorage.getItem('currentProjectId');
  const proposalId = localStorage.getItem('currentProposalId');

  // Alert functions
  const showAlert = (type, message, duration = 3000) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), duration);
  };

  const dismissAlert = () => {
    setAlert({ show: false, type: '', message: '' });
  };

  // Load initial data + dist/taluka/village from selectedProposal
  useEffect(() => {
    try {
      const sel = localStorage.getItem('selectedProposal');
      if (sel) {
        const p = JSON.parse(sel);
        proposalDefaultsRef.current = {
          district: p.dist_name || p.distName || '',
          taluka: p.taluka_name || p.talukaName || '',
          village: p.village_name || p.villageName || ''
        };
        if (proposalDefaultsRef.current.district || proposalDefaultsRef.current.taluka || proposalDefaultsRef.current.village) {
          setShowFilters(true);
        }
      }
    } catch (err) {
      console.error('Error parsing selectedProposal defaults:', err);
    }

    if (projectId && proposalId) {
      loadDirectPurchaseData();
      loadDistricts();
      loadAreaUnits();
      loadLandTypes();
    } else {
      showAlert('error', 'Project ID and Proposal ID are required');
    }
  }, []);


  useEffect(() => {
    if (shouldFetchData()) {
      loadDirectPurchaseData();
    }
  }, [filterDistrict, filterTaluka, filterVillage]);

  // Validation function - all fields mandatory except remarks (शेरा)
  const validateForm = () => {
    const errors = {};

    if (!formData.registration_date?.trim()) {
      errors.registration_date = 'नोंदणी तारीख आवश्यक आहे';
    }

    if (!formData.registration_no?.trim()) {
      errors.registration_no = 'नोंदणी क्रमांक आवश्यक आहे';
    }

    if (!selectedDistrict?.trim()) {
      errors.dist_name = 'जिल्हा निवडणे आवश्यक आहे';
    }

    if (!selectedTaluka?.trim()) {
      errors.taluka_name = 'तालुका निवडणे आवश्यक आहे';
    }

    if (!selectedVillage?.trim()) {
      errors.village_name = 'गाव निवडणे आवश्यक आहे';
    }

    if (!formData.owner_name?.trim()) {
      errors.owner_name = 'मालकाचे नाव आवश्यक आहे';
    }

    if (!formData.owner_address?.trim()) {
      errors.owner_address = 'मालकाचा पत्ता आवश्यक आहे';
    }

    if (!formData.payee_name?.trim()) {
      errors.payee_name = 'देणदाराचे नाव आवश्यक आहे';
    }

    if (!formData.payee_address?.trim()) {
      errors.payee_address = 'देणदाराचा पत्ता आवश्यक आहे';
    }

    if (!formData.survey_no?.trim()) {
      errors.survey_no = 'सर्व्हे क्रमांक आवश्यक आहे';
    }

    if (!formData.irrigable_area?.trim()) {
      errors.irrigable_area = 'बागायती क्षेत्र आवश्यक आहे';
    }

    if (!formData.size_rupees?.trim()) {
      errors.size_rupees = 'आकार (रुपयात) आवश्यक आहे';
    }

    if (!formData.area_sold?.trim()) {
      errors.area_sold = 'विकलेले क्षेत्र आवश्यक आहे';
    }

    if (!formData.selling_price?.trim()) {
      errors.selling_price = 'विक्री किंमत आवश्यक आहे';
    }

    if (!formData.hectare_rate_amount?.trim()) {
      errors.hectare_rate_amount = 'हेक्टरी किंमत आवश्यक आहे';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Load districts + from selectedProposal
  const loadDistricts = async () => {
    try {
      setLoadingDistricts(true);
      const response = await apiService.getDistricts();
      const districtsData = response.data || response || [];
      const districtsArray = Array.isArray(districtsData) ? districtsData : [];
      setDistricts(districtsArray);

      const defaults = proposalDefaultsRef.current;
      if (defaults && defaults.district) {
        const found = districtsArray.find(d =>
          d.dist_name === defaults.district || d.name === defaults.district || d.eng_dist_name === defaults.district
        );

        const valueToSet = found ? (found.dist_name || found.name) : defaults.district;

        if (!filterDistrict) setFilterDistrict(valueToSet);
        if (!selectedDistrict) setSelectedDistrict(valueToSet);
      }
    } catch (error) {
      console.error('Error loading districts:', error);
      setDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  // Load talukas when district changes (for modal)
  useEffect(() => {
    if (selectedDistrict) {
      loadTalukas();
    } else {
      setAvailableTalukas([]);
      setSelectedTaluka('');
      setSelectedVillage('');
      setAvailableVillages([]);
    }
  }, [selectedDistrict]);

  const loadTalukas = async () => {
    try {
      setLoadingTalukas(true);
      const selectedDistrictObj = districts.find(d =>
        d.name === selectedDistrict || d.dist_name === selectedDistrict
      );

      if (!selectedDistrictObj) {
        console.error('Selected district not found:', selectedDistrict);
        return;
      }

      const distNo = selectedDistrictObj.dist_name;
      const response = await apiService.getTalukas(distNo);
      const talukasData = response.data || response || [];
      const talukasArray = Array.isArray(talukasData) ? talukasData : [];
      setAvailableTalukas(talukasArray);
    } catch (error) {
      console.error('Error loading talukas:', error);
      setAvailableTalukas([]);
    } finally {
      setLoadingTalukas(false);
    }
  };

  // Load villages when taluka changes (for modal)
  useEffect(() => {
    if (selectedTaluka && selectedDistrict) {
      loadVillages();
    } else {
      setAvailableVillages([]);
      setSelectedVillage('');
    }
  }, [selectedTaluka, selectedDistrict]);

  const loadVillages = async () => {
    try {
      setLoadingVillages(true);

      const selectedDistrictObj = districts.find(d => d.dist_name === selectedDistrict);

      const selectedTalukaObj = availableTalukas.find(t => t.taluka_name === selectedTaluka);

      if (!selectedDistrictObj || !selectedTalukaObj) {
        console.warn('Matching district or taluka not found', {
          selectedDistrict,
          selectedTaluka,
          districts,
          availableTalukas
        });
        return;
      }

      const districtNo = selectedDistrictObj.dist_name;
      const talukaNo = selectedTalukaObj.taluka_name;

      const result = await apiService.getVillages(districtNo, talukaNo);

      if (result.status === 200 && result.data) {
        setAvailableVillages(result.data);
      } else {
        setAvailableVillages([]);
      }
    } catch (error) {
      console.error('Error loading villages:', error);
      setAvailableVillages([]);
    } finally {
      setLoadingVillages(false);
    }
  };

  // filter taluka useEffect and function
  useEffect(() => {
    if (filterDistrict) {
      loadTalukasForFilter();
    } else {
      setAvailableTalukas([]);
      setFilterTaluka('');
      setFilterVillage('');
      setAvailableVillages([]);
    }
  }, [filterDistrict]);

  const loadTalukasForFilter = async () => {
    try {
      setLoadingTalukas(true);
      const districtObj = districts.find(d => d.dist_name === filterDistrict || d.name === filterDistrict);
      if (!districtObj) return;

      const distName = districtObj.dist_name;
      const response = await apiService.getTalukas(distName);
      const data = Array.isArray(response.data) ? response.data : [];
      setAvailableTalukas(data);

      const defaults = proposalDefaultsRef.current;
      if (defaults && defaults.taluka && !filterTaluka) {
        const found = data.find(t =>
          t.taluka_name === defaults.taluka || t.tehsil_name === defaults.taluka || t.eng_tehsil_name === defaults.taluka
        );
        const valueToSet = found ? (found.taluka_name || found.tehsil_name) : defaults.taluka;
        setFilterTaluka(valueToSet);
        if (!selectedTaluka) setSelectedTaluka(valueToSet);
      }
    } catch (error) {
      console.error('Failed to load talukas for filter:', error);
      setAvailableTalukas([]);
    } finally {
      setLoadingTalukas(false);
    }
  };

  useEffect(() => {
    if (filterDistrict && filterTaluka) {
      loadVillagesForFilter();
    } else {
      setAvailableVillages([]);
      setFilterVillage('');
    }
  }, [filterTaluka]);

  const loadVillagesForFilter = async () => {
    try {
      setLoadingVillages(true);
      const districtObj = districts.find(d => d.dist_name === filterDistrict || d.name === filterDistrict);
      const talukaObj = availableTalukas.find(t => t.taluka_name === filterTaluka || t.tehsil_name === filterTaluka);

      if (!districtObj || !talukaObj) return;

      const distName = districtObj.dist_name;
      const talukaName = talukaObj.taluka_name || talukaObj.tehsil_name;

      const response = await apiService.getVillages(distName, talukaName);
      const data = Array.isArray(response.data) ? response.data : [];
      setAvailableVillages(data);

      const defaults = proposalDefaultsRef.current;
      if (defaults && defaults.village && !filterVillage) {
        const found = data.find(v =>
          v.village_name === defaults.village ||
          v.Village_Name === defaults.village ||
          v.eng_village_name === defaults.village ||
          v.name === defaults.village
        );
        const valueToSet = found ? (found.village_name || found.Village_Name || found.name) : defaults.village;
        setFilterVillage(valueToSet);
        if (!selectedVillage) setSelectedVillage(valueToSet);
      }
    } catch (error) {
      console.error('Failed to load villages for filter:', error);
      setAvailableVillages([]);
    } finally {
      setLoadingVillages(false);
    }
  };

  // ========== MODAL DROPDOWN HANDLERS (FIX: syncs selectedDistrict/Taluka/Village) ==========
  const handleModalDistrictChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, dist_name: value, taluka_name: '', village_name: '' }));
    setSelectedDistrict(value);
    setSelectedTaluka('');
    setSelectedVillage('');
  };

  const handleModalTalukaChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, taluka_name: value, village_name: '' }));
    setSelectedTaluka(value);
    setSelectedVillage('');
  };

  const handleModalVillageChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, village_name: value }));
    setSelectedVillage(value);
  };

  // ========== MARKET VALUE AUTO-FETCH ON SURVEY NO ==========
  const fetchMarketValueForSurvey = async (surveyNo) => {
    if (!surveyNo || !surveyNo.trim()) return;
    if (!selectedDistrict || !selectedTaluka || !selectedVillage) return;
    if (!projectId || !proposalId) return;

    try {
      setLoadingMarketValue(true);

      const response = await apiService.getOwnerMarketValue(
        projectId,
        proposalId,
        {
          distName: selectedDistrict,
          talukaName: selectedTaluka,
          villName: selectedVillage,
          surveyNo: surveyNo.trim(),
        }
      );

      if (response.status === 200 && response.data) {
        const record = Array.isArray(response.data) ? response.data[0] : response.data;

        // Map actual API response fields:
        // market_group_no → size_gat (आकार गट)
        // rate_per_hectare → hectare_rate (हेक्टरी आकारणीचा दर)
        // group_valuation → size_rupees (आकार रु.पै)
        // irrigable_area → irrigable_area (लागवडयोग्य क्षेत्रफळ)
        // market_amount → hectare_rate_amount (हेक्टरी किंमत)
        const sizeGat = record?.market_group_no != null ? String(record.market_group_no) : '';
        const hectareRate = record?.rate_per_hectare != null
          ? String(record.rate_per_hectare)
          : '';
        const groupValuation = record?.group_valuation != null
          ? String(record.group_valuation)
          : '';
        const irrigableArea = record?.irrigable_area != null
          ? String(record.irrigable_area)
          : '';
        const hectareRateAmount = record?.market_amount != null
          ? String(record.market_amount)
          : '';

        setFormData((prev) => ({
          ...prev,
          size_gat: sizeGat || prev.size_gat,
          hectare_rate: hectareRate || prev.hectare_rate,
          size_rupees: groupValuation || prev.size_rupees,
          irrigable_area: irrigableArea || prev.irrigable_area,
          hectare_rate_amount: hectareRateAmount || prev.hectare_rate_amount,
        }));

        showAlert('success', 'बाजारभाव माहिती आपोआप भरली गेली');
      }
    } catch (error) {
      console.error('Error fetching market value for survey:', error);
    } finally {
      setLoadingMarketValue(false);
    }
  };

  // Handle survey_no change with debounce
  const handleSurveyNoChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, survey_no: value }));

    // Clear previous timeout
    if (surveyNoTimeoutRef.current) {
      clearTimeout(surveyNoTimeoutRef.current);
    }

    // Debounce: wait 800ms after user stops typing
    surveyNoTimeoutRef.current = setTimeout(() => {
      fetchMarketValueForSurvey(value);
    }, 800);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (surveyNoTimeoutRef.current) {
        clearTimeout(surveyNoTimeoutRef.current);
      }
    };
  }, []);

  // ========== UNDO FUNCTIONALITY ==========
  const handleUndo = async () => {
    if (undoStack.length === 0) {
      showAlert('warning', 'पूर्ववत करण्यासाठी कोणतीही क्रिया नाही');
      return;
    }

    const lastAction = undoStack[undoStack.length - 1];

    try {
      setLoading(true);

      if (lastAction.type === 'add') {
        // Undo add = delete the record
        const response = await apiService.deleteDirectPurchase(lastAction.recordId);
        if (response?.status === 200) {
          showAlert('success', 'नवीन नोंद यशस्वीरित्या पूर्ववत केली');
        }
      } else if (lastAction.type === 'edit') {
        // Undo edit = restore previous data
        const response = await apiService.patchUpdateDirectPurchase(lastAction.recordId, lastAction.previousData);
        if (response?.status === 200) {
          showAlert('success', 'संपादन यशस्वीरित्या पूर्ववत केले');
        }
      }

      // Remove the last action from undo stack
      setUndoStack((prev) => prev.slice(0, -1));
      // Reload data
      loadDirectPurchaseData();
    } catch (error) {
      console.error('Error during undo:', error);
      showAlert('error', 'पूर्ववत करताना त्रुटी आली');
    } finally {
      setLoading(false);
    }
  };

  const openProposalEditModal = () => {
    const selectedProposal = JSON.parse(localStorage.getItem("selectedProposal")) || {};
    setProposalEditData({
      section11_rehabilitation_rights_notice: selectedProposal.section11_rehabilitation_rights_notice || "",
      direct_purchase_deputy_register_no: selectedProposal.direct_purchase_deputy_register_no || "",
      direct_purchase_deputy_register_name: selectedProposal.direct_purchase_deputy_register_name || ""
    });
    setIsProposalEditing(true);
  };

  const handleProposalEditChange = (e) => {
    const { name, value } = e.target;
    setProposalEditData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProposalEditSubmit = async () => {
    try {
      setSavingProposal(true);
      const proposalIdVal = proposalDetails?.acquisition_proposal_id;

      if (!proposalIdVal) {
        showAlert("error", "Proposal ID not found in proposalDetails");
        return;
      }

      const payload = {
        section11_rehabilitation_rights_notice: proposalEditData.section11_rehabilitation_rights_notice || null,
        direct_purchase_deputy_register_no: proposalEditData.direct_purchase_deputy_register_no || null,
        direct_purchase_deputy_register_name: proposalEditData.direct_purchase_deputy_register_name || null,
      };

      const response = await apiService.updateProposalPublish(proposalIdVal, payload);

      if (response.ok) {
        showAlert("success", "✅ Proposal details updated successfully!");
        setIsProposalEditing(false);

        const updatedProposal = { ...proposalDetails, ...payload };
        setProposalDetails(updatedProposal);

        localStorage.setItem("selectedProposal", JSON.stringify(updatedProposal));

        return true;
      } else {
        showAlert("error", "⚠️ Failed to update proposal details");
        return false;
      }
    } catch (error) {
      console.error("Error updating proposal:", error);
      showAlert("error", "❌ Update failed: " + error.message);
      return false;
    } finally {
      setSavingProposal(false);
    }
  };

  const loadAreaUnits = async () => {
    try {
      const response = await apiService.getAreaUnits();
      setAreaUnits(response.data || response || []);
    } catch (error) {
      console.error('Error loading area units:', error);
    }
  };

  const loadLandTypes = async () => {
    try {
      const response = await apiService.getLandTypes();
      setLandTypes(response.data || response || []);
    } catch (error) {
      console.error('Error loading land types:', error);
    }
  };

  const loadDirectPurchaseData = async () => {
    try {
      setLoading(true);
      setError('');

      const locationParams = {};
      if (filterDistrict) locationParams.distName = filterDistrict;
      if (filterTaluka) locationParams.talukaName = filterTaluka;
      if (filterVillage) locationParams.villName = filterVillage;

      const response = await apiService.getDirectPurchaseKJP(projectId, proposalId, locationParams);

      if (response.status === 204 || !response.data || response.data.length === 0) {
        setDirectPurchaseData([]);
      } else {
        setDirectPurchaseData(response.data);
      }
    } catch (error) {
      console.error('Error loading direct purchase data:', error);
      setError('Failed to load direct purchase data');
      setDirectPurchaseData([]);
      showAlert('error', 'Failed to load direct purchase data');
    } finally {
      setLoading(false);
    }
  };

  const shouldFetchData = () => {
    if (!projectId || !proposalId) return false;

    if (filterDistrict && !filterTaluka && !filterVillage) return true;
    if (filterDistrict && filterTaluka && !filterVillage) return true;
    if (filterDistrict && filterTaluka && filterVillage) return true;

    return false;
  };

  const clearFilters = () => {
    setFilterDistrict('');
    setFilterTaluka('');
    setFilterVillage('');
    setAvailableTalukas([]);
    setAvailableVillages([]);
    loadDirectPurchaseData();
  };

  // Modal functions
  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentRecord(null);

    setFormData({
      registration_date: "",
      registration_no: "",
      owner_name: "",
      owner_address: "",
      payee_name: "",
      payee_address: "",
      survey_no: "",
      irrigable_area: "",
      size_rupees: "",
      area_sold: "",
      selling_price: "",
      hectare_rate: "",
      hectare_rate_amount: "",
      size_gat: "",
      remarks: "",
      dist_name: filterDistrict || "",
      taluka_name: filterTaluka || "",
      village_name: filterVillage || "",
    });

    setSelectedDistrict(filterDistrict || "");
    setSelectedTaluka(filterTaluka || "");
    setSelectedVillage(filterVillage || "");

    setShowModal(true);
  };

  const openEditModal = (record) => {
    setIsEditMode(true);
    setCurrentRecord(record);

    setFormData({
      registration_date: record.registration_date ? record.registration_date.split("T")[0] : "",
      registration_no: record.registration_no || "",
      owner_name: record.owner_name || "",
      owner_address: record.owner_address || "",
      payee_name: record.payee_name || "",
      payee_address: record.payee_address || "",
      survey_no: record.survey_no || "",
      irrigable_area: record.irrigable_area || "",
      size_rupees: record.size_rupees || "",
      area_sold: record.area_sold || "",
      selling_price: record.selling_price || "",
      hectare_rate: record.hectare_rate || "",
      hectare_rate_amount: record.hectare_rate_amount || "",
      size_gat: record.size_gat || "",
      remarks: record.remarks || "",
      dist_name: record.dist_name || "",
      taluka_name: record.taluka_name || "",
      village_name: record.village_name || "",
    });

    setSelectedDistrict(record.dist_name || "");
    setSelectedTaluka(record.taluka_name || "");
    setSelectedVillage(record.village_name || "");

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setCurrentRecord(null);
    loadDirectPurchaseData();
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Calculate total land value
  useEffect(() => {
    if (formData.land_area && formData.land_value_rate) {
      const total = parseFloat(formData.land_area) * parseFloat(formData.land_value_rate);
      setFormData(prev => ({ ...prev, land_value_total: total.toString() }));
    }
  }, [formData.land_area, formData.land_value_rate]);

  // Handle form submission with undo support
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showAlert('error', 'कृपया सर्व आवश्यक फील्ड भरा');
      return;
    }

    try {
      setLoading(true);

      const submitData = {
        project_id: parseInt(projectId),
        proposal_id: parseInt(proposalId),
        ...formData,
        dist_name: selectedDistrict,
        taluka_name: selectedTaluka,
        village_name: selectedVillage,
      };

      let response;
      if (isEditMode) {
        // Save previous data for undo before editing
        const previousData = {
          project_id: parseInt(projectId),
          proposal_id: parseInt(proposalId),
          registration_date: currentRecord.registration_date ? currentRecord.registration_date.split("T")[0] : "",
          registration_no: currentRecord.registration_no || "",
          owner_name: currentRecord.owner_name || "",
          owner_address: currentRecord.owner_address || "",
          payee_name: currentRecord.payee_name || "",
          payee_address: currentRecord.payee_address || "",
          survey_no: currentRecord.survey_no || "",
          irrigable_area: currentRecord.irrigable_area || "",
          size_rupees: currentRecord.size_rupees || "",
          area_sold: currentRecord.area_sold || "",
          selling_price: currentRecord.selling_price || "",
          hectare_rate: currentRecord.hectare_rate || "",
          hectare_rate_amount: currentRecord.hectare_rate_amount || "",
          size_gat: currentRecord.size_gat || "",
          remarks: currentRecord.remarks || "",
          dist_name: currentRecord.dist_name || "",
          taluka_name: currentRecord.taluka_name || "",
          village_name: currentRecord.village_name || "",
        };

        response = await apiService.patchUpdateDirectPurchase(currentRecord.direct_purchase_id, submitData);
        if (response?.status === 200) {
          // Push to undo stack
          setUndoStack((prev) => [...prev, {
            type: 'edit',
            recordId: currentRecord.direct_purchase_id,
            previousData: previousData,
            newData: submitData,
          }]);
          showAlert('success', 'नोंद यशस्वीरित्या अद्यतनित केली!');
          loadDirectPurchaseData();
        }
      } else {
        response = await apiService.postAddDirectPurchase(submitData);
        if (response?.status === 200) {
          // Get the new record ID from response for undo
          const newRecordId = response.data?.direct_purchase_id || response.data?.id;
          setUndoStack((prev) => [...prev, {
            type: 'add',
            recordId: newRecordId,
            newData: submitData,
          }]);
          showAlert('success', 'नवीन नोंद यशस्वीरित्या जोडली!');
        }
      }

      if (response?.status === 200) {
        setTimeout(() => {
          closeModal();
          loadDirectPurchaseData();
        }, 500);
      }

    } catch (error) {
      console.error('Error submitting form:', error);
      showAlert('error', `Failed to ${isEditMode ? 'update' : 'add'} direct purchase record`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatFromDate = (toDateStr) => {
    if (!toDateStr) return "";
    const d = new Date(toDateStr);
    d.setFullYear(d.getFullYear() - 3);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // ========== GROUP DATA BY DIST → TALUKA → VILLAGE ==========
  const getGroupedData = () => {
    if (!directPurchaseData || directPurchaseData.length === 0) return [];

    const groupMap = {};

    directPurchaseData.forEach((record) => {
      const key = `${record.dist_name || ''}||${record.taluka_name || ''}||${record.village_name || ''}`;
      if (!groupMap[key]) {
        groupMap[key] = {
          dist_name: record.dist_name || '',
          taluka_name: record.taluka_name || '',
          village_name: record.village_name || '',
          records: [],
        };
      }
      groupMap[key].records.push(record);
    });

    // Sort groups by dist → taluka → village
    return Object.values(groupMap).sort((a, b) => {
      if (a.dist_name !== b.dist_name) return a.dist_name.localeCompare(b.dist_name);
      if (a.taluka_name !== b.taluka_name) return a.taluka_name.localeCompare(b.taluka_name);
      return a.village_name.localeCompare(b.village_name);
    });
  };

  const getDistrictDisplayName = (district) => {
    return district.dist_name || district.name || '';
  };

  const getTalukaDisplayName = (taluka) => {
    return taluka.tehsil_name || taluka.name || '';
  };

  const getVillageDisplayName = (village) => {
    return village.village_name || village.name || '';
  };

  const handlePrint = () => {
    if (!directPurchaseData || directPurchaseData.length === 0) {
      return;
    }

    const printContent = document.getElementById("notice-content")?.innerHTML;
    if (!printContent || printContent.trim() === "") {
      return;
    }

    const win = window.open("", "", "height=900,width=1200");
    win.document.write(`
    <html>
    <head>
      <meta charset="UTF-8">
      <title>खरेदी विक्री तक्ता -दुरुस्ती</title>
      <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&display=swap" rel="stylesheet">
      <style>
        @page {
          size: A3 landscape;
          margin-top: 0.19in;
          margin-bottom: 0.25in;
          margin-left: 0.81in;
          margin-right: 0.5in;
        }
        body {
          font-family: 'DVOT-Surekh', 'Mangal', 'Arial', sans-serif;
          font-size: 12pt;
          line-height: 1.4;
        }
        * { box-sizing: border-box; }
        #notice-content { padding: 1rem; }
        h1, h2, h3, h4, h5, h6 { margin: 1rem 0 0.5rem 0; page-break-after: avoid; font-weight: bold; }
        h2 { font-size: 16pt; text-align: center; margin-top: 1rem; margin-bottom: 1rem; }
        h3 { text-align: center; margin: 0; font-size: 14pt; }
        .flex-1 { flex: 1; text-align: center; padding-right: 2rem; margin-top: 0.25rem; }
        .font-semibold { font-weight: 600; font-size: 11pt; }
        .font-bold { font-weight: bold; }
        .font-medium { font-weight: 500; }
        .flex { display: flex; }
        .justify-between { justify-content: space-between; }
        .justify-center { justify-content: center; }
        .items-end { align-items: flex-end; }
        .gap-4 { gap: 1rem; }
        .ml-28 { margin-left: 7rem; }
        .mr-20 { margin-right: 5rem; }
        .ml-5 { margin-left: 1.25rem; }
        .mr-5 { margin-right: 1.25rem; }
        .mt-1 { margin-top: 0.25rem; }
        .mt-2 { margin-top: 0.5rem; }
        .mt-3 { margin-top: 0.75rem; }
        .mt-4 { margin-top: 1rem; }
        .mt-6 { margin-top: 1.5rem; }
        .mt-8 { margin-top: 2rem; }
        .mt-10 { margin-top: 2rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-4 { margin-bottom: 1rem; }
        .mb-6 { margin-bottom: 1.5rem; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .text-base { font-size: 11pt; }
        .text-sm { font-size: 10pt; }
        .text-xs { font-size: 9pt; }
        .text-xl { font-size: 1.5rem; }
        .text-2xl { font-size: 1.8rem; }
        .underline { text-decoration: underline; }
        .indent-8 { text-indent: 2rem; }
        table { width: 100%; border-collapse: collapse; border: 1px solid black; margin-top: 10px; margin-bottom: 1rem; page-break-inside: auto !important; font-size: 10pt; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; page-break-after: auto; }
        th, td { border: 1px solid black; padding: 4px; text-align: center; vertical-align: middle; font-size: 9pt; page-break-inside: avoid; }
        th { background-color: #f9fafb; font-weight: bold; font-size: 10pt; }
        .border { border: 1px solid black; }
        .p-2 { padding: 0.5rem; }
        p { margin: 0.5rem 0; line-height: 1.6; orphans: 3; widows: 3; font-size: 11pt; }
        br { line-height: 1.2; }
        .space-y-4 > * + * { margin-top: 1rem; }
        .mx-auto { margin-left: auto; margin-right: auto; }
        .border-black { border: 1.5px solid black; }
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table { page-break-inside: auto !important; font-size: 10px; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          th, td { padding: 2px; page-break-inside: avoid; page-break-after: auto; }
          .page-break { page-break-after: always; }
          .page-break-avoid { page-break-inside: avoid; break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      ${printContent}
    </body>
    </html>
  `);
    win.document.close();
    win.print();
  };

  const handleContactChange = (e) => {
    let input = e.target.value.replace(/\D/g, '');
    let error = '';
    let isMobile = false;

    if (input.length === 10 && /^[6-9]\d{9}$/.test(input)) {
      isMobile = true;
    } else if (input.length === 11 && /^0[1-9]\d{9}$/.test(input)) {
      error = '';
    } else if (input.length === 10 || input.length === 11) {
      error = 'Invalid number format';
    } else {
      error = '';
    }

    setIsMobileValid(isMobile);

    setFormData((prev) => ({
      ...prev,
      owner_contact: input,
    }));

    setContactError(error);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Alert */}
        {alert.show && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${alert.type === "success"
              ? "bg-green-100 text-green-800 border border-green-200"
              : alert.type === "error"
                ? "bg-red-100 text-red-800 border border-red-200"
                : alert.type === "warning"
                  ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                  : "bg-blue-100 text-blue-800 border border-blue-200"
              }`}
          >
            {alert.type === "success" ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span>{alert.message}</span>
            <button onClick={dismissAlert} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-4 py-2 bg-gradient-to-r from-orange-700 to-orange-500 hover:from-orange-700 hover:to-orange-700 text-white rounded-lg flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-gray-200 hover:shadow-gray-300 group"
              >
                <Filter className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
                <span className="font-medium">{translations[language].Filters}</span>
              </button>

              {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                <>
                  <button
                    onClick={openAddModal}
                    className="px-5 py-3 text-sm bg-gradient-to-r from-blue-600 to-gray-900 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-blue-200 hover:shadow-blue-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
                    <span className="font-medium">{translations[language].AddDirectPurchaseRecord}</span>
                  </button>

                  <button
                    onClick={openProposalEditModal}
                    className="px-5 py-3 text-sm bg-gradient-to-r from-green-600 to-gray-900 hover:from-green-700 hover:to-green-800 text-white rounded-xl flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-green-200 hover:shadow-green-300 group"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L7.5 21H3v-4.5L16.732 3.732z" />
                    </svg>
                    <span className="font-medium">{translations[language].DirectPurchaseInfo}</span>
                  </button>

                  {/* Undo Button */}
                  
                </>
              )}
              <button
                onClick={handlePrint}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Printer size={18} />
                <span>{translations[language]?.Download}</span>
              </button>
            </div>

            {/* {(selectedDistrict || selectedTaluka || selectedVillage) && (
              <button
                onClick={clearFilters}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-300"
              >
                Clear Filters
              </button>
            )} */}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* District Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language].District}
                  </label>
                  <select
                    value={filterDistrict}
                    onChange={(e) => setFilterDistrict(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={loadingDistricts}
                  >
                    <option value="">{translations[language].SelectDistrict}</option>
                    {districts.map((district, index) => (
                      <option
                        key={index}
                        value={getDistrictDisplayName(district)}
                      >
                        {getDistrictDisplayName(district)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Taluka Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language].Taluka}
                  </label>
                  <select
                    value={filterTaluka}
                    onChange={(e) => setFilterTaluka(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!filterDistrict || loadingTalukas}
                  >
                    <option value="">{translations[language].SelectTaluka}</option>
                    {availableTalukas.map((taluka) => (
                      <option key={taluka.taluka_no} value={taluka.taluka_name}>
                        {taluka.taluka_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Village Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations[language].Village}
                  </label>
                  <select
                    value={filterVillage}
                    onChange={(e) => setFilterVillage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={!filterTaluka || loadingVillages}
                  >
                    <option value="">{translations[language].SelectVillage}</option>
                    {availableVillages.map((village) => (
                      <option
                        key={village.village_id}
                        value={village.village_name}
                      >
                        {village.village_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4"></div>
            </div>
          )}
        </div>

        {/* Data Table - Grouped by Dist/Taluka/Village */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">
                {translations[language].LoadingDirectPurchaseRecords}
              </p>
            </div>
          ) : directPurchaseData.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">{translations[language].NoDirectPurchaseRecordsFound}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div id="notice-content" className="p-4">
                {getGroupedData().map((group, groupIndex) => (
                  <div key={`${group.dist_name}-${group.taluka_name}-${group.village_name}`} className={groupIndex > 0 ? "page-break mt-10" : ""}>
                    {/* Group Header */}
                    <h2 className="text-xl font-bold text-center mt-4">
                      जमिनीचे खरेदी/विक्री व्यवहारांची माहिती
                    </h2>
                    {proposalDetails && (
                      <div className="flex-1 text-center pr-8">
                        <p className="font-semibold">
                          भूसंपादन प्रकरण क्रमांक : {proposalDetails.section_11_bhusampadan_no || ""} <br />
                          <div className="flex justify-between ml-28 mr-20">
                            <div>गाव - {group.village_name}</div>
                            <div>ता. {group.taluka_name}</div>
                            जि. {group.dist_name}
                          </div>
                          <div>
                            व्यवहाराचा कालावधी :- दिनांक{" "}
                            {proposalDetails?.section11_rehabilitation_rights_notice
                              ? formatFromDate(proposalDetails.section11_rehabilitation_rights_notice)
                              : "____"}{" "}
                            ते{" "}
                            {proposalDetails?.section11_rehabilitation_rights_notice
                              ? formatDate(proposalDetails.section11_rehabilitation_rights_notice)
                              : "____"}
                          </div>
                        </p>
                      </div>
                    )}

                    {/* Group Table */}
                    <table className="w-full border-collapse border border-gray-400">
                      <thead>
                        <tr>
                          {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                            <th rowSpan="2" className="px-4 py-4 text-center font-bold text-gray-700 bg-gray-50 min-w-[120px] no-print">
                              कृती
                            </th>
                          )}
                          <th className="border p-2">अ.क्र</th>
                          <th className="border p-2">दस्तऐवज नोंदणी <br />दिनांक</th>
                          <th className="border p-2">दस्तऐवज<br />नोंदणी <br />क्रमांक</th>
                          <th className="border p-2">खरेदी देणाराचे नाव व <br /> पत्ता</th>
                          <th className="border p-2">खरेदी घेणाराचे नाव व <br /> पत्ता</th>
                          <th className="border p-2">गट क्रमांक/<br /> सर्व्हे क्रमांक</th>
                          <th className="border p-2">गट क्र./सर्व्हे क्र.चे <br />लागवडयोग्य<br />क्षेत्रफळ<br />(हे.आर)</th>
                          <th className="border p-2">आकार <br />(रु.पै )</th>
                          <th className="border p-2">विक्री केलेले<br />क्षेत्रफळ<br /> (हे.आर.)</th>
                          <th className="border p-2">विक्री किंमत</th>
                          <th className="border p-2">हेक्टरी<br />आकारणीचा<br /> दर (रु.पै )</th>
                          <th className="border p-2">हेक्टरी किंमत <br />(रकाना क्र.10<br />मधील<br />किंमतीच्या<br />तुलनेत)</th>
                          <th className="border p-2">आकार <br />गट</th>
                          <th className="border p-2">शेरा</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                            <td className="border p-2 text-center no-print"></td>
                          )}
                          <td className="border p-2 text-center">1</td>
                          <td className="border p-2 text-center">2</td>
                          <td className="border p-2 text-center">3</td>
                          <td className="border p-2 text-center">4</td>
                          <td className="border p-2 text-center">5</td>
                          <td className="border p-2 text-center">6</td>
                          <td className="border p-2 text-center">7</td>
                          <td className="border p-2 text-center">8</td>
                          <td className="border p-2 text-center">9</td>
                          <td className="border p-2 text-center">10</td>
                          <td className="border p-2 text-center">11</td>
                          <td className="border p-2 text-center">12</td>
                          <td className="border p-2 text-center">13</td>
                          <td className="border p-2 text-center">14</td>
                        </tr>
                        {group.records.map((record, index) => (
                          <tr key={record.direct_purchase_id}>
                            {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                              <td className="border p-2 text-center no-print">
                                <button
                                  onClick={() => openEditModal(record)}
                                  className="px-4 py-2 text-sm bg-gradient-to-r from-green-600 to-gray-900 hover:from-green-700 hover:to-green-800 text-white rounded-lg flex items-center space-x-2 transition-all duration-300 shadow-md shadow-green-200 hover:shadow-green-300 group"
                                >
                                  <span><Pencil size={16} /></span>
                                </button>
                              </td>
                            )}
                            <td className="border p-2">{index + 1}</td>
                            <td className="border p-2">{formatDate(record.registration_date)}</td>
                            <td className="border p-2">{record.registration_no}</td>
                            <td className="border p-2">
                              {record.owner_name}<br />{record.owner_address}
                            </td>
                            <td className="border p-2">
                              {record.payee_name}<br />{record.payee_address}
                            </td>
                            <td className="border p-2">{record.survey_no}</td>
                            <td className="border p-2">{record.irrigable_area}</td>
                            <td className="border p-2">{record.size_rupees}</td>
                            <td className="border p-2">{record.area_sold}</td>
                            <td className="border p-2">{record.selling_price}</td>
                            <td className="border p-2">{record.hectare_rate}</td>
                            <td className="border p-2">{record.hectare_rate_amount}</td>
                            <td className="border p-2">{record.size_gat}</td>
                            <td className="border p-2">{record.remarks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Group Footer */}
                    <div>
                      <p className="ml-5 mt-3">
                        प्रमाणित करण्यात येते की, उक्त माहितीमध्ये दिनांक{" "}
                        {proposalDetails?.section11_rehabilitation_rights_notice
                          ? formatFromDate(proposalDetails.section11_rehabilitation_rights_notice)
                          : "____"}{" "}
                        ते{" "}
                        {proposalDetails?.section11_rehabilitation_rights_notice
                          ? formatDate(proposalDetails.section11_rehabilitation_rights_notice)
                          : "____"}{" "}
                        या कालावधीमधील शेतजमिनीच्या सर्व नोंदणीकृत खरेदी विक्री व्यवहारांचा समावेश
                        करण्यात आला असून या कालावधीतील कोणताही नोंदणीकृत व्यवहार यातून सुटलेला नाही.
                        तसेच संपूर्ण तपशील अचूक असल्याबाबत खात्री करण्यात आली आहे.
                      </p>
                    </div>

                    <p className="text-right mt-2 mr-5">
                      दुय्यम निबंधक श्रेणी 1<br />
                      {proposalDetails?.direct_purchase_deputy_register_name || ""}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isEditMode ? "नोंद अद्यतनित करा" : "नवीन नोंद जोडा"}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* District */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      जिल्हा *
                    </label>
                    <select
                      name="dist_name"
                      value={selectedDistrict}
                      onChange={handleModalDistrictChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.dist_name ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={loadingDistricts}
                    >
                      <option value="">जिल्हा निवडा</option>
                      {districts.map((d, i) => (
                        <option key={i} value={getDistrictDisplayName(d)}>
                          {getDistrictDisplayName(d)}
                        </option>
                      ))}
                    </select>
                    {formErrors.dist_name && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.dist_name}</p>
                    )}
                  </div>

                  {/* Taluka */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      तालुका *
                    </label>
                    <select
                      name="taluka_name"
                      value={selectedTaluka}
                      onChange={handleModalTalukaChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.taluka_name ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={!selectedDistrict || loadingTalukas}
                    >
                      <option value="">तालुका निवडा</option>
                      {availableTalukas.map((t) => (
                        <option key={t.taluka_no} value={t.taluka_name}>
                          {t.taluka_name}
                        </option>
                      ))}
                    </select>
                    {formErrors.taluka_name && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.taluka_name}</p>
                    )}
                  </div>

                  {/* Village */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      गाव *
                    </label>
                    <select
                      name="village_name"
                      value={selectedVillage}
                      onChange={handleModalVillageChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.village_name ? 'border-red-500' : 'border-gray-300'}`}
                      disabled={!selectedTaluka || loadingVillages}
                    >
                      <option value="">गाव निवडा</option>
                      {availableVillages.map((v) => (
                        <option key={v.village_id} value={v.village_name}>
                          {v.village_name}
                        </option>
                      ))}
                    </select>
                    {formErrors.village_name && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.village_name}</p>
                    )}
                  </div>

                  {/* Registration Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      दस्तऐवज नोंदणी दिनांक *
                    </label>
                    <input
                      type="date"
                      name="registration_date"
                      value={formData.registration_date}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.registration_date ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.registration_date && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.registration_date}</p>
                    )}
                  </div>

                  {/* Registration Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      दस्तऐवज नोंदणी क्रमांक *
                    </label>
                    <input
                      type="text"
                      name="registration_no"
                      value={formData.registration_no}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.registration_no ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.registration_no && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.registration_no}</p>
                    )}
                  </div>

                  {/* Seller Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      खरेदी देणाराचे नाव *
                    </label>
                    <input
                      type="text"
                      name="payee_name"
                      value={formData.payee_name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.payee_name ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.payee_name && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.payee_name}</p>
                    )}
                  </div>

                  {/* Seller Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      खरेदी देणाराचे राहण्याचे ठिकाण *
                    </label>
                    <textarea
                      name="payee_address"
                      value={formData.payee_address}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.payee_address ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.payee_address && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.payee_address}</p>
                    )}
                  </div>

                  {/* Buyer Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      खरेदी घेणाराचे नाव *
                    </label>
                    <input
                      type="text"
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.owner_name ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.owner_name && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.owner_name}</p>
                    )}
                  </div>

                  {/* Buyer Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      खरेदी घेणाराचे राहण्याचे ठिकाण *
                    </label>
                    <textarea
                      name="owner_address"
                      value={formData.owner_address}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.owner_address ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.owner_address && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.owner_address}</p>
                    )}
                  </div>

                  {/* Survey No - auto-fetch market value */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      गट नंबर / सर्व्हे नंबर *
                      {loadingMarketValue && (
                        <span className="ml-2 text-blue-500 text-xs animate-pulse">बाजारभाव शोधत आहे...</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="survey_no"
                      value={formData.survey_no}
                      onChange={handleSurveyNoChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.survey_no ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.survey_no && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.survey_no}</p>
                    )}
                  </div>

                  {/* Irrigable Area */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ग न/स न चे ला.योग्य क्षेत्रफळ (हे.आर) *
                      {formData.irrigable_area && loadingMarketValue === false && formData.survey_no && (
                        <span className="text-xs text-green-600 ml-1">(स्वयंचलित)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="irrigable_area"
                      value={formData.irrigable_area}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.irrigable_area ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.irrigable_area && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.irrigable_area}</p>
                    )}
                  </div>

                  {/* Size (₹) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      आकार (रु.पै) *
                      {formData.size_rupees && loadingMarketValue === false && formData.survey_no && (
                        <span className="text-xs text-green-600 ml-1">(स्वयंचलित)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="size_rupees"
                      value={formData.size_rupees}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.size_rupees ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.size_rupees && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.size_rupees}</p>
                    )}
                  </div>

                  {/* Area Sold */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      विक्री केलेल क्षेत्रफळ (हे.आर.) *
                    </label>
                    <input
                      type="text"
                      name="area_sold"
                      value={formData.area_sold}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.area_sold ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.area_sold && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.area_sold}</p>
                    )}
                  </div>

                  {/* Selling Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      विक्री किंमत *
                    </label>
                    <input
                      type="text"
                      name="selling_price"
                      value={formData.selling_price}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.selling_price ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.selling_price && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.selling_price}</p>
                    )}
                  </div>

                  {/* Hectare Rate - Auto-filled */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      हेक्टरी आकारणीचा दर (रु.पै)
                      {formData.hectare_rate && (
                        <span className="text-xs text-green-600 ml-1">(स्वयंचलित)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="hectare_rate"
                      value={formData.hectare_rate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      readOnly
                    />
                  </div>

                  {/* Hectare Price */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      हेटरी किंमत (रकाना क्र.10 मधील किंमतीच्या तुलनेत) *
                      {formData.hectare_rate_amount && loadingMarketValue === false && formData.survey_no && (
                        <span className="text-xs text-green-600 ml-1">(स्वयंचलित)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="hectare_rate_amount"
                      value={formData.hectare_rate_amount}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg ${formErrors.hectare_rate_amount ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {formErrors.hectare_rate_amount && (
                      <p className="text-red-500 text-sm mt-1">{formErrors.hectare_rate_amount}</p>
                    )}
                  </div>

                  {/* Size Group - Auto-filled */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      आकार गट
                      {formData.size_gat && (
                        <span className="text-xs text-green-600 ml-1">(स्वयंचलित)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="size_gat"
                      value={formData.size_gat}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                      readOnly
                    />
                  </div>

                  {/* Remarks */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      शेरा
                    </label>
                    <textarea
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    disabled={loading}
                    className={`px-6 py-2 border border-gray-300 rounded-lg ${loading ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50"}`}
                  >
                    रद्द करा
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`px-6 py-2 rounded-lg text-white ${loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"}`}
                  >
                    {loading ? "Saving..." : isEditMode ? "अद्यतनित करा" : "जोडा"}
                  </button>
                </div>

              </form>
            </div>
          </div>
        )}

        {isProposalEditing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-800">{translations[language].DirectPurchaseInfo}</h3>
                  <button
                    onClick={() => setIsProposalEditing(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl text-right font-bold"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleProposalEditSubmit}>
                  <div className="mb-6">
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      व्यावहाराचा कालावधी * :
                    </label>
                    <div className="flex gap-4">
                      <input
                        type="date"
                        name="section11_rehabilitation_rights_notice"
                        value={proposalEditData.section11_rehabilitation_rights_notice || ""}
                        onChange={handleProposalEditChange}
                        className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none w-1/2"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      दुय्यम निबंधक क्रमांक * :
                    </label>
                    <input
                      type="text"
                      name="direct_purchase_deputy_register_no"
                      value={proposalEditData.direct_purchase_deputy_register_no || ""}
                      onChange={handleProposalEditChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block font-semibold text-gray-700 mb-2 text-base">
                      दुय्यम निबंधक श्रेणी 1 * :
                    </label>
                    <input
                      type="text"
                      name="direct_purchase_deputy_register_name"
                      value={proposalEditData.direct_purchase_deputy_register_name || ""}
                      onChange={handleProposalEditChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none"
                    />
                  </div>

                  <div className="flex justify-end gap-4 border-t pt-6">
                    <button
                      type="button"
                      onClick={() => setIsProposalEditing(false)}
                      className="px-6 py-3 border rounded-xl"
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      disabled={savingProposal}
                      onClick={async () => {
                        const success = await handleProposalEditSubmit();
                        if (success) {
                          await new Promise((res) => requestAnimationFrame(res));
                          handlePrint();
                        }
                      }}
                      className={`px-8 py-3 rounded-xl text-white ${savingProposal ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-600 to-indigo-600"}`}
                    >
                      {savingProposal ? "Saving..." : "Save Changes"}
                    </button>
                  </div>

                </form>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DirectPayment;